import React, { useEffect, useState, useImperativeHandle, forwardRef, useMemo } from 'react';
import api from '../api/client';
import PlanPopup from './PlanPopup';
import CanProducePopup from './CanProducePopup';
import DemandCoveragePopup from './DemandCoveragePopup';
import PopupDailyPlanSets from './PopupDailyPlanSets';
import './SmallBagsDailyPlan.css';

// Все производство планируется по Майами — "сегодня" считаем в America/New_York,
// а даты храним строками 'YYYY-MM-DD', чтобы не было сдвига на день из-за toISOString()
const todayNYStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

const SmallBagsDailyPlan = forwardRef(({ viewMode, setViewMode, searchTerm = '' }, ref) => {
  const [dataRows, setDataRows] = useState([]);
  const [allDateColumns, setAllDateColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [canProducePopupData, setCanProducePopupData] = useState(null);
  const [demandPopupData, setDemandPopupData] = useState(null);
  const [plannedSetsPopupData, setPlannedSetsPopupData] = useState(null);
  const [plannedProduction, setPlannedProduction] = useState({});
  const [productionStatus, setProductionStatus] = useState({});
  const [dailyTotals, setDailyTotals] = useState({});
  const [totalDemand, setTotalDemand] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [setsCompositionMap, setSetsCompositionMap] = useState({});
  const [sets, setSets] = useState([]);
  const [isTotalDemandCollapsed, setIsTotalDemandCollapsed] = useState(true);
  const [sortOrder, setSortOrder] = useState('none'); // 'none', 'asc', 'desc'

  const handleSortStock = () => {
    setSortOrder(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const [filters, setFilters] = useState({
    daysToShow: 30,
    rowFilterRange: null,
    selectedSets: [],
    demandForSelectedSets: false,
    excludePlanned: false,
    showOnlyPlanned: false,
    bagShortageDays: null,
    seedsShortageDays: null,
    hideNonSetPackets: true,
  });
  // Будни показываем всегда, выходные — только если на них уже что-то запланировано
  const dateColumns = useMemo(() => {
    const todayStr = todayNYStr();
    const end = new Date(`${todayStr}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() + filters.daysToShow);
    const endStr = end.toISOString().split('T')[0];
    return allDateColumns.filter(col => {
      if (col.dateStr < todayStr || col.dateStr > endStr) return false;
      const isWeekend = col.date.getDay() === 0 || col.date.getDay() === 6;
      return !isWeekend || (dailyTotals[col.dateStr]?.totalQty || 0) > 0;
    });
  }, [allDateColumns, filters.daysToShow, dailyTotals]);

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const response = await api.get('/api/sets');
        setSets(response.data.items || []);
      } catch (error) {
        console.error('Error fetching sets:', error);
        setErrorMessage('Failed to load sets.');
      }
    };
    
const fetchSetsComposition = async () => {
  try {
    const response = await api.get('/api/daily-plan-setscomposition');
    console.log('API Response:', response.data);
    
    const map = {};
    // Используем response.data.items (даже если он пуст)
    const items = response.data.items || [];
    
    items.forEach(item => {
      if (item.sbSKU) {
        map[item.sbSKU] = map[item.sbSKU] || [];
        map[item.sbSKU].push(item.setSKU);
      }
    });
    
    setSetsCompositionMap(map);
    console.log('Sets Composition Map:', map);
  } catch (error) {
    console.error('Error fetching sets composition:', error);
    if (error.response) {
      console.error('Server response:', error.response.data);
      setErrorMessage(`Backend error: ${error.response.data.error}`);
    } else {
      setErrorMessage('Network error loading sets composition');
    }
  }
};
    fetchSets();
    fetchSetsComposition();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters.daysToShow]);

  const fetchMainData = async () => {
    try {
      const response = await api.get('/api/get_smallbags_dailyplan', { timeout: 20000 });
      const rows = response.data.values || [];
      if (rows.length < 1) {
        setDataRows([]);
        setAllDateColumns([]);
        return;
      }

      const headers = rows[0] || [];
      const dateHeaders = headers.slice(3);
      const tempDateColumns = dateHeaders.map((dateStr, index) => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        const parsedDate = new Date(year, month - 1, day);
        if (isNaN(parsedDate.getTime())) return null;
        return { dateIndex: index, date: parsedDate, dateStr };
      }).filter(col => col !== null);
      // qIdx — позиция колонки в полном массиве; quantities/left индексируются по ней,
      // потому что видимые dateColumns могут пропускать выходные
      tempDateColumns.forEach((col, i) => { col.qIdx = i; });

      setAllDateColumns(tempDateColumns);

      const tempDataRows = rows.slice(1).map(row => {
        const quantities = [];
        const ordered = [];
        const leftPlusOrder = [];
        tempDateColumns.forEach(col => {
          const baseIndex = 3 + (col.dateIndex * 3);
          quantities.push(Number(row[baseIndex]) || 0);
          ordered.push(Number(row[baseIndex + 1]) || 0);
          leftPlusOrder.push(Number(row[baseIndex + 2]) || 0);
        });
        return {
          name: row[0] || 'Unknown',
          SKU: row[1] || 'Unknown',
          stock: Number(row[2]) || 0,
          quantities,
          ordered,
          leftPlusOrder,
          canProduce: 0,
          bagsAvailable: 0,
          seedsAvailable: 0,
          seedsInTreatment: 0,
          seedsUnderGermination: 0,
          gperpacket: 0
        };
      });

      const updatedRows = await Promise.all(tempDataRows.map(async row => {
        try {
          const response = await api.get(`/api/get_can_produce_details?sku=${row.SKU}`);
          return {
            ...row,
            // 'fert' = пакетик удобрения для машины (fert_products.machine_packet), его нет в составах сетов
            productType: response.data.productType || 'seeds',
            canProduce: response.data.canProduce || 0,
            bagsAvailable: response.data.bagsAvailable || 0,
            seedsAvailable: response.data.seedsAvailable || 0,
            seedsInTreatment: response.data.seedsInTreatment || 0,
            seedsUnderGermination: response.data.seedsUnderGermination || 0,
            gperpacket: response.data.gperpacket || 0
          };
        } catch (error) {
          console.error(`Error fetching can produce for SKU ${row.SKU}:`, error);
          return row;
        }
      }));

      setDataRows(updatedRows);
    } catch (error) {
      console.error('Error loading main data:', error);
      setErrorMessage('Failed to load main data.');
    }
  };

  const fetchPlannedProduction = async () => {
    try {
      const plannedResponse = await api.get('/api/get_planned_production', { timeout: 10000 });
      const plannedData = {};
      const totals = {};
      const skuSets = {};
      (plannedResponse.data || []).forEach(item => {
        const [month, day, year] = item.date.split('/');
        const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        const key = `${item.sbsku}_${formattedDate}`;
        plannedData[key] = { qty: item.qty, id: item.id, useSubstitute: !!item.use_substitute };
        const dateKey = formattedDate;
        if (!totals[dateKey]) {
          totals[dateKey] = { totalQty: 0, skuCount: 0 };
          skuSets[dateKey] = new Set();
        }
        totals[dateKey].totalQty += Number(item.qty) || 0;
        if (item.qty > 0) skuSets[dateKey].add(item.sbsku);
        totals[dateKey].skuCount = skuSets[dateKey].size;
      });
      setPlannedProduction(plannedData);
      setDailyTotals(totals);
    } catch (error) {
      console.error('Error loading planned production:', error);
      setErrorMessage('Failed to load planned production.');
    }
  };

  const fetchProductionStatus = async () => {
    try {
      const statusResponse = await api.get('/api/get_production_status', { timeout: 10000 });
      const statusData = {};
      (statusResponse.data || []).forEach(item => {
        const key = `${item.sku}_${item.prodDate}`;
        statusData[key] = item.status;
      });
      setProductionStatus(statusData);
    } catch (error) {
      console.error('Error loading production status:', error);
      setErrorMessage('Failed to load production status.');
    }
  };

  const fetchTotalDemand = async () => {
    try {
      const response = await api.get('/api/get_total_demand', { timeout: 10000 });
      setTotalDemand(response.data || {});
    } catch (error) {
      console.error('Error fetching total demand:', error);
      setErrorMessage('Failed to fetch total demand.');
    }
  };

  const fetchPlannedSets = async (col) => {
    try {
      const response = await api.get(`/api/get_masterbags_dailyplan?date=${col.dateStr}`);
      const plannedSets = response.data || [];
      // Filter to include only sets that exist in the sets table
      const validSets = plannedSets.filter(plan => 
        sets.some(set => set.SKU === plan.mbsku)
      ).map(plan => ({
        ...plan,
        setname: sets.find(set => set.SKU === plan.mbsku)?.setname || 'Unknown',
        brandID: sets.find(set => set.SKU === plan.mbsku)?.brandID || 'Unknown'
      }));
      setPlannedSetsPopupData({ date: col.date, sets: validSets });
    } catch (error) {
      console.error('Error fetching planned sets:', error);
      setErrorMessage('Failed to load planned sets.');
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await api.post('/api/move-sbproduction-plan');
      await Promise.all([
        fetchMainData(),
        fetchPlannedProduction(),
        fetchProductionStatus(),
        fetchTotalDemand()
      ]);
    } catch (error) {
      console.error('General data loading error:', error);
      setErrorMessage('Failed to load data.');
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleRefresh: fetchData,
    applyFilters: setFilters,
    getTableData: () => ({
      filteredDataRows: sortedDataRows,
      dateColumns,
      totalDemand,
      plannedProduction,
      productionStatus,
      dailyTotals,
      isTotalDemandCollapsed,
      filters,
    }),
  }));

  const filteredDataRows = dataRows.filter(row => {
    if (searchTerm && !`${row.name} ${row.SKU}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    // «Hide packets not in any set»: пакетик, которого нет ни в одном составе сета (setscomposition),
    // скрыт всегда — ни другие фильтры, ни активный план его не возвращают (02.10.2026; раньше активный
    // план показывал такую строку вопреки галочке). Поле individualSale здесь не участвует: оно устарело,
    // на поштучную продажу идут все пакетики.
    // Пакетики удобрений для машины в составы сетов не входят (они идут в kits), галочка их не прячет
    if (filters.hideNonSetPackets && row.productType !== 'fert' && !(setsCompositionMap[row.SKU] || []).length) return false;

    // Позиции с активным планом (Planned / In production) показываем всегда,
    // независимо от фильтров по дефицитам (Demand Range, Bag / Seeds Shortage)
    const hasActivePlan = dateColumns.some(col => {
      const planKey = `${row.SKU}_${col.dateStr}`;
      const status = productionStatus[planKey] ?? 'Planned';
      return (plannedProduction[planKey]?.qty || 0) > 0 && (status === 'Planned' || status === 'In production');
    });

    if (filters.selectedSets.length > 0 && filters.demandForSelectedSets) {
      const setsForRow = setsCompositionMap[row.SKU] || [];
      if (!setsForRow.some(set => filters.selectedSets.includes(set))) return false;
    }
    if (filters.rowFilterRange && !hasActivePlan) {
      const periods = [1, 3, 7, 14, 30, 90, 180, 365];
      const demandIndex = periods.indexOf(filters.rowFilterRange);
      if (demandIndex !== -1) {
        let demandForPeriod;
        if (demandIndex === 0) {
          demandForPeriod = row.quantities[0] || 0;
        } else if (demandIndex === 1) {
          const numDays = Math.min(3, row.quantities.length);
          demandForPeriod = row.quantities.slice(0, numDays).reduce((a, b) => a + b, 0);
        } else {
          const adjustedIndex = demandIndex - 2;
          const demands = totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0];
          demandForPeriod = demands[adjustedIndex] || 0;
        }
        // Отрицательный остаток считаем нулём, как вся страница (Stock, L, Demand Stock): иначе пакетик
        // с остатком −5 и нулевой потребностью попадал в «не хватает» и показывался с подписью Stock: 0.
        if (Math.max(row.stock, 0) >= demandForPeriod) return false;
      }
    }
    if (filters.excludePlanned && hasActivePlan) return false;
    if (filters.showOnlyPlanned && !hasActivePlan) return false;
    if (filters.bagShortageDays && !hasActivePlan) {
      const periods = [7, 14, 30, 90, 180, 365];
      const demandIndex = periods.indexOf(filters.bagShortageDays);
      if (demandIndex !== -1) {
        const demands = totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0];
        const demandForPeriod = demands[demandIndex] || 0;
        if (row.bagsAvailable >= demandForPeriod) return false;
      }
    }
    if (filters.seedsShortageDays && !hasActivePlan) {
      const periods = [7, 14, 30, 90, 180, 365];
      const demandIndex = periods.indexOf(filters.seedsShortageDays);
      if (demandIndex !== -1) {
        const demands = totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0];
        const demandForPeriod = demands[demandIndex] || 0;
        const packetsFromSeeds = row.seedsAvailable && row.gperpacket ? Math.floor(row.seedsAvailable * 453.592 / row.gperpacket) : 0;
        if (packetsFromSeeds >= demandForPeriod) return false;
      }
    }
    return true;
  });

  const sortedDataRows = useMemo(() => {
    return [...filteredDataRows].sort((a, b) => {
      if (sortOrder === 'none') return 0;
      const stockA = Math.max(a.stock, 0);
      const stockB = Math.max(b.stock, 0);
      return sortOrder === 'asc' ? stockA - stockB : stockB - stockA;
    });
  }, [filteredDataRows, sortOrder]);

  const handleDemandClick = async row => {
    try {
      const response = await api.get(`/api/get_can_produce_details?sku=${row.SKU}`);
      setDemandPopupData({
        sku: row.SKU,
        stock: Math.max(row.stock, 0),
        cbp: response.data.canProduce || 0,
        bagsAvailable: response.data.bagsAvailable || 0,
        seedsAvailable: response.data.seedsAvailable || 0,
        seedsInTreatment: response.data.seedsInTreatment || 0,
        seedsUnderGermination: response.data.seedsUnderGermination || 0,
        gperpacket: response.data.gperpacket || 0,
        demands: totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0],
      });
    } catch (error) {
      console.error(`Error fetching data for SKU ${row.SKU}:`, error);
      setErrorMessage('Failed to load popup data.');
    }
  };

  const calculateRollingLeft = (quantities, stock) => {
    let cumulativeUsed = 0;
    return quantities.map(qty => {
      cumulativeUsed += Number(qty) || 0;
      return Math.max(Number(stock), 0) - cumulativeUsed;
    });
  };

  // leftValues параллелен allDateColumns — план на скрытых выходных тоже учитывается
  const calculateLeftPlusPP = (leftValues, sku) => {
    const todayStr = todayNYStr();
    let cumulativePlanned = 0;
    return leftValues.map((left, idx) => {
      const col = allDateColumns[idx];
      if (col && col.dateStr >= todayStr) {
        const planKey = `${sku}_${col.dateStr}`;
        const status = productionStatus[planKey] ?? 'Planned';
        if (status === 'Planned' || status === 'In production') {
          cumulativePlanned += Number(plannedProduction[planKey]?.qty || 0);
        }
      }
      return left + cumulativePlanned;
    });
  };

  const findCoverageDays = (quantities, startIndex, S) => {
    if (S <= 0) return 0;
    let cumulative = 0;
    let demandDays = 0;
    const start = Math.max(0, startIndex + 1);
    for (let K = 0; K < quantities.length - start; K++) {
      const demand = Number(quantities[start + K]) || 0;
      if (demand > 0) demandDays++;
      cumulative += demand;
      if (cumulative > S) return demandDays;
    }
    return demandDays > 0 ? demandDays : Infinity;
  };

  const findEndStockDate = (quantities, stock, dateColumns) => {
    let cumulative = 0;
    for (let i = 0; i < quantities.length && i < dateColumns.length; i++) {
      cumulative += Number(quantities[i]) || 0;
      if (cumulative > Math.max(stock, 0)) {
        return { index: i, date: dateColumns[i]?.date || null };
      }
    }
    return { index: -1, date: null };
  };

  const handlePlanClick = (row, col) => {
    if (!col) return;
    const planKey = `${row.SKU}_${col.dateStr}`;
    const planned = plannedProduction[planKey] || { qty: 0, id: null };
    setPopupData({
      name: row.name,
      sku: row.SKU,
      date: col.date,
      dateStr: col.dateStr,
      stock: row.stock,
      demands: totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0],
      existingQty: planned.qty,
      existingUseSubstitute: planned.useSubstitute,
      id: planned.id
    });
  };

  const handleCanProduceClick = async row => {
    try {
      const response = await api.get(`/api/get_can_produce_details?sku=${row.SKU}`);
      setCanProducePopupData({
        sku: row.SKU,
        canProduce: response.data.canProduce || 0,
        bagsAvailable: response.data.bagsAvailable || 0,
        seedsAvailable: response.data.seedsAvailable || 0,
        seedsInTreatment: response.data.seedsInTreatment || 0,
        seedsUnderGermination: response.data.seedsUnderGermination || 0
      });
    } catch (error) {
      console.error(`Error fetching can produce details for SKU ${row.SKU}:`, error);
      setErrorMessage('Failed to load Can be Produced data.');
    }
  };

  const handlePlannedSetsClick = (col) => {
    fetchPlannedSets(col);
  };

  const handleSavePlan = async (sku, dateStr, qty, id, useSubstitute) => {
    try {
      await api.post('/api/save_planned_production', {
        id,
        sbsku: sku,
        date: dateStr,
        qty: Number(qty),
        status: 'Planned',
        use_substitute: useSubstitute ? 1 : 0
      });
      setPopupData(null);
      fetchData();
    } catch (error) {
      console.error('Error saving plan:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to save plan.');
    }
  };

  const handleDeletePlan = async (id) => {
    try {
      await api.delete(`/api/sbplanned_production/${id}`);
      setPopupData(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting plan:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to delete plan.');
    }
  };

  const handleClosePopup = () => setPopupData(null);
  const handleCloseCanProducePopup = () => setCanProducePopupData(null);
  const handleClosePlannedSetsPopup = () => setPlannedSetsPopupData(null);
  const toggleTotalDemand = () => setIsTotalDemandCollapsed(prev => !prev);

  if (isLoading) {
    return (
      <div className="daily-plan-table-container">
        <div className="preloader-overlay">
          <div className="preloader">
            <div className="spinnerChooser"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-plan-table-container">
      {errorMessage && (
        <div className="error-message" style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>
          {errorMessage}
        </div>
      )}
      {popupData && popupData.date && (
        <PlanPopup
          name={popupData.name}
          date={popupData.date}
          dateStr={popupData.dateStr}
          sku={popupData.sku}
          stock={popupData.stock}
          demands={popupData.demands}
          existingQty={popupData.existingQty}
          existingUseSubstitute={popupData.existingUseSubstitute}
          id={popupData.id}
          plannedProduction={plannedProduction}
          onSave={handleSavePlan}
          onDelete={handleDeletePlan}
          onClose={handleClosePopup}
        />
      )}
      {canProducePopupData && (
        <CanProducePopup
          {...canProducePopupData}
          onClose={handleCloseCanProducePopup}
        />
      )}
      {demandPopupData && (
        <DemandCoveragePopup
          data={demandPopupData}
          onClose={() => setDemandPopupData(null)}
        />
      )}
      {plannedSetsPopupData && (
        <PopupDailyPlanSets
          date={plannedSetsPopupData.date}
          setsData={plannedSetsPopupData.sets}
          onClose={handleClosePlannedSetsPopup}
        />
      )}
      {dateColumns.length === 0 ? (
        <div>No data available for the next {filters.daysToShow} days</div>
      ) : (
        <table className="table-daily-plan sb-plan-table">
          <thead>
            <tr>
              <th rowSpan="2" className="sticky-col name-header">Name</th>
              <th rowSpan="2" className="sticky-col stock-header">
                <div className="stock-header-content">
                  <span>Stock</span>
                  <button
                    className={`sort-btn sort-btn-${sortOrder}`}
                    onClick={handleSortStock}
                    title={`Sort by Stock (${sortOrder === 'asc' ? 'Ascending' : sortOrder === 'desc' ? 'Descending' : 'None'})`}
                  >
                    <i className={`fa fa-sort${sortOrder === 'asc' ? '-up' : sortOrder === 'desc' ? '-down' : ''}`}></i>
                  </button>
                </div>
              </th>
              <th rowSpan="2" className="total-demand-header">
                <span className="header-toggle" onClick={toggleTotalDemand}>
                  Demand Stock
                  <i className={`fa fa-${isTotalDemandCollapsed ? 'plus' : 'minus'} toggle-icon`}></i>
                </span>
              </th>
              {!isTotalDemandCollapsed && (
                <th rowSpan="2" className="total-demand-header">Total Demand</th>
              )}
              {dateColumns.map(col => {
                const isWeekend = col.date.getDay() === 0 || col.date.getDay() === 6;
                return (
                  <th key={col.dateIndex} className={`date-header${isWeekend ? ' weekend' : ''}`} colSpan={viewMode === 'all' ? 2 : 1}>
                    <div className="date-total">
                      <span>{`${col.date.getMonth() + 1}/${col.date.getDate()}/${col.date.getFullYear()}`}</span>
                      <div>
                        <span className={`total ${dailyTotals[col.dateStr]?.totalQty > 70000 ? 'high-total' : 'low-total'}`}>
                          {dailyTotals[col.dateStr]?.totalQty.toLocaleString() || 0}
                        </span>
                        <span className={`total ${dailyTotals[col.dateStr]?.skuCount > 10 ? 'high-total' : 'low-total'}`}>
                          /{dailyTotals[col.dateStr]?.skuCount || 0}
                        </span>
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
            <tr>
              {dateColumns.map(col => (
                <React.Fragment key={col.dateIndex}>
                  <th>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {viewMode === 'all' ? 'Demand' : viewMode === 'demand' ? 'Demand' : viewMode === 'left' ? 'Left' : 'Left + PP'}
                      <svg
                        className="planned-sets-icon"
                        style={{ cursor: 'pointer', fill: '#666', width: '16px', height: '16px' }}
                        onClick={() => handlePlannedSetsClick(col)}
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="m14.1865234 6.3691406c-.1738281-.9916992-.3115234-1.7749023-.3115234-2.8691406v-3c0-.2070313-.1679688-.375-.375-.375h-11c-.2070313 0-.375.1679688-.375.375v3c0 1.0942383-.1376953 1.8774414-.3115234 2.8691406-.2905274 1.6547852-.6884766 3.9213867-.6884766 9.1308594 0 .2070313.1679688.375.375.375h13c.2070313 0 .375-.1679688.375-.375 0-5.2094727-.3979492-7.4760742-.6884766-9.1308594zm-1.0615234-3.2441406h-10.25v-2.25h10.25zm-10.5727539 3.3735352c.1594238-.9089356.2833252-1.6618653.3111572-2.6235352h10.2731934c.027832.9616699.1517334 1.7145996.3111572 2.6235352.2789307 1.586914.6566162 3.753601.6754761 8.6264648h-12.24646c.0188599-4.8728638.3965454-7.0395508.6754761-8.6264648z"/>
                      </svg>
                    </div>
                  </th>
                  {viewMode === 'all' && <th className="planning-table-pp-value">PP</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedDataRows.map((row, rowIndex) => {
              const leftValues = calculateRollingLeft(row.quantities, row.stock);
              const leftPlusPPValues = calculateLeftPlusPP(leftValues, row.SKU);
              const totalStock = Math.max(row.stock, 0) + row.canProduce;
              const demands = totalDemand[row.SKU] || [0, 0, 0, 0, 0, 0];
              const { index: endStockIndex, date: endStockDate } = findEndStockDate(row.quantities, row.stock, allDateColumns);
              const productionDemandDays = findCoverageDays(row.quantities, -1, Math.max(row.stock, 0));
              const colorClass = endStockDate === null || productionDemandDays === Infinity ? 'green' :
                                productionDemandDays <= 1 ? 'red' :
                                productionDemandDays <= 5 ? 'yellow' : 'green';
              const plannedDetails = dateColumns
                .map((col, colIndex) => {
                  const planKey = `${row.SKU}_${col.dateStr}`;
                  const planned = plannedProduction[planKey];
                  const status = productionStatus[planKey] ?? 'Planned';
                  if (planned && planned.qty > 0 && (status === 'Planned' || status === 'In production')) {
                    return {
                      id: planned.id,
                      useSubstitute: planned.useSubstitute,
                      detail: `${status}: ${planned.qty.toLocaleString()}, Start Date: ${col.date.toLocaleDateString()}`
                    };
                  }
                  return null;
                })
                .filter(detail => detail !== null);

              return (
                <tr key={rowIndex}>
                  <td className="name-cell">
                    <div>
                      <div>{row.name}</div>
                      {plannedDetails.length > 0 && (
                        <div className="planned-production-details">
                          {plannedDetails.map((detail, index) => (
                            <div key={index}>
                              {/* один span: ячейки таблицы раскладывают детей div в столбик */}
                              <span>
                                {detail.detail}
                                {detail.useSubstitute && (
                                  <i className="fa-solid fa-right-left" title="Planned with substitute seeds"
                                    style={{ marginLeft: 6, color: '#d97706' }}></i>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="stock-cell">
                    <div className="stock-details">
                      <div>Stock: {Math.max(row.stock, 0).toLocaleString()}</div>
                      <div className="can-produce" onClick={() => handleCanProduceClick(row)}>
                        CBP: {row.canProduce.toLocaleString()}
                      </div>
                      <div className="total-stock">
                        Total: {totalStock.toLocaleString()}
                      </div>
                      <div className={`end-stock-date ${colorClass}`}>
                        End date: <strong>{endStockDate ? endStockDate.toLocaleDateString() : 'Sufficient stock'}</strong>
                      </div>
                    </div>
                  </td>
                  <td className="total-demand-cell" onClick={() => handleDemandClick(row)} style={{ cursor: 'pointer' }}>
                    <div className="demand-grid">
                      {demands.map((demand, index) => {
                        const periods = [7, 14, 30, 90, 180, 365];
                        const isGreen = Math.max(row.stock, 0) > demand;
                        return (
                          <div key={index} className={`demand-value ${isGreen ? 'green' : 'red'}`}>
                            {periods[index]} d: {demand.toLocaleString()}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  {!isTotalDemandCollapsed && (
                    <td className="total-demand-cell" onClick={() => handleDemandClick(row)} style={{ cursor: 'pointer' }}>
                      <div className="demand-grid">
                        {demands.map((demand, index) => {
                          const periods = [7, 14, 30, 90, 180, 365];
                          const isGreen = totalStock > demand;
                          return (
                            <div key={index} className={`demand-value ${isGreen ? 'green' : 'red'}`}>
                              {periods[index]} d: {demand.toLocaleString()}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  )}
                  {dateColumns.map((col, colIndex) => {
                    const planKey = `${row.SKU}_${col.dateStr}`;
                    const plannedQty = plannedProduction[planKey]?.qty || 0;
                    const status = productionStatus[planKey] ?? 'Planned';
                    const L = leftValues[col.qIdx];
                    const LPP = leftPlusPPValues[col.qIdx];
                    const K_L = findCoverageDays(row.quantities, col.qIdx, L);
                    const K_LPP = findCoverageDays(row.quantities, col.qIdx, LPP);
                    const getColorClass = K => (K >= 3 || K === Infinity ? 'green' : K >= 1 ? 'yellow' : 'red');
                    const LClass = `left-value-${getColorClass(K_L)}`;
                    const LPPClass = `left-pp-value-${getColorClass(K_LPP)}`;

                    return (
                      <React.Fragment key={col.dateIndex}>
                        <td>
                          {viewMode === 'all' ? (
                            <div>
                              <span className="demand-value">D: {row.quantities[col.qIdx].toLocaleString()}</span>
                              <span className={`demand-value ${LClass}`}>L: {L.toLocaleString()}</span>
                              <span className={`demand-value ${LPPClass}`}>L+PP: {LPP.toLocaleString()}</span>
                            </div>
                          ) : viewMode === 'demand' ? (
                            <span className="demand-value">D: {row.quantities[col.qIdx].toLocaleString()}</span>
                          ) : viewMode === 'left' ? (
                            <span className={`demand-value ${LClass}`}>L: {L.toLocaleString()}</span>
                          ) : viewMode === 'left_pp' ? (
                            <span className={`demand-value ${LPPClass}`}>L+PP: {LPP.toLocaleString()}</span>
                          ) : null}
                        </td>
                        {viewMode === 'all' && (
                          <td className="planning-table-pp-value">
                            <button
                              className={`planning-btn-add-order ${plannedQty > 0 && status !== 'Planned' ? 'disabled' : ''}`}
                              onClick={() => handlePlanClick(row, col)}
                              disabled={plannedQty > 0 && status !== 'Planned'}
                            >
                              {plannedQty === 0 ? '+' : status === 'Planned' ? <i className="fa fa-edit"></i> : '+'}
                            </button>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
});

export default SmallBagsDailyPlan;